import React, { useEffect, useState } from "react";

import { ApiCallStatus, ApiEndpoints } from "../../../api-http/types";
import { useAuthContext } from "../../../auth";
import { useLabels } from "../../../contexts/labels";
import { useGetConsents } from "../../../hooks/use-get-consents";
import { useGetGenericApiValues } from "../../../hooks/use-get-generic-api-values";
import { useGetUserInfo } from "../../../hooks/use-get-user-data";
import { renameFields } from "../../../utils/object";
import { Container } from "../../container";
import { Spinner } from "../../spinner";
import {
  PersonalAreaProfileFormFieldsLabels,
  PersonalAreaProfileFormFieldsTypes,
} from "../forms-fields/personal-area-profile-form-fields/types";
import { PersonalAreaProfileForm } from "./form";
import { mapApiPayloadToFormData, mapFormDataToApiPayload } from "./map-form-data-to-api-payload";
import { ResponseModal, ResponseModalLabels } from "./response-modal";
import { usePatchUserInfo } from "./use-patch-user-info";

export interface PersonalAreaProfileFormSectionProps {
  lang: string;
}

export function PersonalAreaProfileFormSection({ lang }: PersonalAreaProfileFormSectionProps) {
  const { labels: stringLabels } = useLabels();

  const fieldsToRename: Record<
    keyof Omit<
      PersonalAreaProfileFormFieldsLabels,
      "personal-area-password-reset-cta" | "consents"
    >,
    string
  > = {
    firstName: "forms-first-name",
    lastName: "forms-last-name",
    email: "forms-email",
    phone: "forms-phone",
    company: "forms-company",
    materials: "forms-materials",
    country: "forms-country",
    zipCode: "forms-zip-code",
    state: "forms-state",
    optionalLabel: "forms-optional-label",
    requiredFieldMessage: "forms-required-field-message",
    wrongPhoneFormat: "forms-wrong-phone-format",
    wrongEmailFormat: "forms-wrong-email-format",
    selectCountryPlaceholder: "forms-select-country-placeholder",
    businessRelationship: "forms-business-relationship",
    city: "forms-city",
    submit: "forms-profile-update-data",
  };

  const labels = renameFields(
    { ...stringLabels },
    fieldsToRename
  ) as PersonalAreaProfileFormFieldsLabels;

  const [materialsList, materialsCallStatus] = useGetGenericApiValues(lang, ApiEndpoints.MATERIALS);
  const [businessRelationshipList, businessRelationshipCallStatus] = useGetGenericApiValues(
    lang,
    ApiEndpoints.BUSINESS_RELATIONSHIP
  );
  const [countriesList, countryCallStatus] = useGetGenericApiValues(lang, ApiEndpoints.COUNTRY);
  const [statesList, stateCallsStatus] = useGetGenericApiValues(lang, ApiEndpoints.PROVINCES);

  const [consents, consentsStatus] = useGetConsents(lang);

  const { userInfo, checking } = useAuthContext();

  const [defaultValues, setDefaultValues] = useState<PersonalAreaProfileFormFieldsTypes>();
  const [formCallsStatus, setFormCallsStatus] = useState<ApiCallStatus>(ApiCallStatus.LOADING);
  const [userData, userCallStatus, userDataError] = useGetUserInfo(userInfo?.sub, lang);

  useEffect(() => {
    userData && setDefaultValues({ ...mapApiPayloadToFormData(userData) });
  }, [userData, setDefaultValues]);

  useEffect(() => {
    const statusArray = [
      materialsCallStatus,
      businessRelationshipCallStatus,
      countryCallStatus,
      stateCallsStatus,
      consentsStatus,
      userCallStatus,
    ];
    if (statusArray.every((x) => x === ApiCallStatus.SUCCESS))
      setFormCallsStatus(ApiCallStatus.SUCCESS);
    else if (statusArray.some((x) => x === ApiCallStatus.FAILED))
      setFormCallsStatus(ApiCallStatus.FAILED);
    else if (checking || statusArray.some((x) => x === ApiCallStatus.LOADING))
      setFormCallsStatus(ApiCallStatus.LOADING);
  }, [
    materialsCallStatus,
    businessRelationshipCallStatus,
    countryCallStatus,
    stateCallsStatus,
    consentsStatus,
    userCallStatus,
    checking,
  ]);

  const [patchUserInfo, patchUserStatus] = usePatchUserInfo();
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  const handleOnSubmit = (data: PersonalAreaProfileFormFieldsTypes) => {
    userInfo?.sub && patchUserInfo(userInfo.sub, mapFormDataToApiPayload(data));
    setIsResponseModalOpen(true);
  };

  return formCallsStatus === ApiCallStatus.LOADING ? (
    <Spinner size="large" className="mx-auto" />
  ) : formCallsStatus === ApiCallStatus.SUCCESS ? (
    <Container className="py-8 bg-neutral-light" pagePaddingX>
      <div className="max-w-4xl mx-auto">
        <PersonalAreaProfileForm
          consents={consents}
          labels={labels}
          onSubmit={handleOnSubmit}
          defaultValues={defaultValues}
          businessRelationshipList={businessRelationshipList}
          countriesList={countriesList}
          materialsList={materialsList}
          statesList={statesList}
        />
      </div>
      <ResponseModal
        isOpen={isResponseModalOpen}
        responseStatus={patchUserStatus}
        labels={stringLabels as unknown as ResponseModalLabels}
      />
    </Container>
  ) : (
    <div className="text-lg text-dark font-light mx-auto p-8 text-center">
      {userDataError && userDataError < 500 && userDataError >= 400
        ? labels["api-network-error-user-not-found"]
        : labels["api-network-error"]}
    </div>
  );
}
